import { atom } from 'jotai'

/**
 * Whether the Meeting View's delete confirmation is showing. The trigger lives
 * in the floating bar but the dialog covers its positioned parent, so it has to
 * mount inside the view surface — in the floating bar it would only scrim the
 * ~40px control row.
 */
export const meetingDeletePendingAtom = atom(false)

/**
 * How much room the open notepad is taking: its header row alone, the resting
 * panel over the chat input, or the whole panel below the header.
 *
 * One field rather than a `collapsed` flag beside a `fullscreen` one, so the
 * two can't disagree — there is no such thing as collapsed *and* full.
 */
export type MeetingNotepadSize = 'collapsed' | 'normal' | 'full'

/**
 * The Notepad's footer mode (HIG-5829): which meeting has it open, by notepad
 * key, and how much room it's taking. Null is the normal chat input.
 *
 * One value rather than an open flag beside a size — the size only means
 * anything while something is open, so nesting it makes opening a fresh mode
 * rather than two writes that have to agree.
 */
export interface MeetingNotepadMode {
  key: string
  size: MeetingNotepadSize
  /**
   * Server identity for the meeting's notepad record (HIG-5815), captured by
   * whichever toggle opened it — the footer-mounted notepad has no meeting
   * model of its own to resolve these from. While a session id is present the
   * notepad syncs to `PUT /audio-sessions/:id/notepad`; a note id alone means
   * a reopened saved meeting, whose notepad document rides on the note.
   */
  audioSessionId?: string
  audioNoteId?: string
}

export const meetingNotepadModeAtom = atom<MeetingNotepadMode | null>(null)

/**
 * Resize the open notepad, if one is open. Write-only, because every caller
 * wants the same two things and neither belongs in a component:
 *
 * - a closed notepad is left closed, so no caller spells out the "spread the
 *   mode, leave null alone" dance (the title row's collapse, the header's full
 *   height button, and the drag on the top edge all did);
 * - a size that is already set writes nothing. The drag calls this on every
 *   pointermove, and a fresh mode object 60 times a second would re-render
 *   every reader of the mode — both notepad toggles, the claim hosts, and the
 *   editor itself — for a value that didn't change.
 */
export const setMeetingNotepadSizeAtom = atom(null, (get, set, size: MeetingNotepadSize) => {
  const current = get(meetingNotepadModeAtom)
  if (!current || current.size === size) return
  set(meetingNotepadModeAtom, { ...current, size })
})
